import { forwardRef } from "react";
import { CAMPAIGN } from "@/lib/campaign";
import { scrollToId } from "@/lib/scroll";
import { RegistrationForm } from "@/components/RegistrationForm";
import { SectionBg } from "@/components/SectionBg";

/* SECTION 1 — HERO */
export const Hero = forwardRef((props, heroRef) => (
  <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-16 px-5 md:px-10 lg:px-16 overflow-hidden">
    <SectionBg img="bg-home-4.jpg" strength={0.9} />
    <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
      {/* LEFT */}
      <div className="text-left">
        <p className="mask-line font-mono text-[11px] md:text-xs tracking-[0.3em] text-[var(--accent)] mb-4">
          <span style={{ animationDelay: "0.05s" }}>FREE LIVE VA HOMEBUYING WORKSHOP · FLORIDA</span>
        </p>
        <p className="mask-line font-mono text-[10px] md:text-[11px] tracking-[0.12em] text-[var(--muted)] mb-8">
          <span style={{ animationDelay: "0.15s" }}>
            {CAMPAIGN.webinarDate} · {CAMPAIGN.webinarTime} · {CAMPAIGN.webinarTimezone}
          </span>
        </p>

        <h1 className="mb-8">
          <span className="mask-line font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-white">
            <span style={{ animationDelay: "0.25s" }}>UNDERSTAND THE BENEFIT</span>
          </span>
          <span className="mask-line font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-[var(--accent)]">
            <span style={{ animationDelay: "0.35s" }}>BEFORE YOU SHOP—</span>
          </span>
          <span className="mask-line font-mono font-normal tracking-[0.06em] text-[15px] sm:text-lg lg:text-[2.33rem] leading-snug text-white/60 mt-4">
            <span style={{ animationDelay: "0.45s" }}>
              ELIGIBILITY · BUYING POWER · CASH CATEGORIES · NEXT STEPS
            </span>
          </span>
        </h1>

        <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.8)] max-w-xl mb-7">
          A <span className="text-[var(--accent)]">free live Florida workshop</span> that organizes the
          questions behind VA eligibility, entitlement, buying power, potential cash needed, credit, the
          purchase process, and the <span className="text-[var(--accent)]">next three actions</span>.
        </p>

        <blockquote className="font-display italic text-lg md:text-xl leading-relaxed text-[rgba(255,255,255,0.72)] border-l-2 border-[var(--accent)] pl-5 max-w-xl mb-9">
          What if the most important part of using a VA home loan benefit is not a headline about zero
          down—but understanding the full path before you choose it?
        </blockquote>

        <div className="mb-3">
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToId("register")}
            className="glint bg-[var(--accent)] text-[var(--background-deep)] font-semibold text-sm tracking-[0.12em] px-8 py-4 rounded-[4px] hover:brightness-110 hover:-translate-y-[1px] transition-[transform,filter] duration-200"
          >
            RESERVE MY FREE SEAT
          </button>
        </div>
        <p className="font-mono text-[10px] tracking-[0.08em] text-[var(--muted)] mb-10">
          No cost to attend · Educational event · No obligation
        </p>

        <div className="flex items-center gap-4" data-testid="hero-host-row">
          <img
            src={CAMPAIGN.headshot}
            alt={`${CAMPAIGN.hostName}, ${CAMPAIGN.hostCompany}`}
            className="w-12 h-12 rounded-[6px] object-cover object-top border border-[var(--line)]"
          />
          <div className="text-left">
            <p className="text-sm font-medium text-white">{CAMPAIGN.hostName}</p>
            <p className="font-mono text-[10px] tracking-[0.1em] text-[var(--muted)]">
              {CAMPAIGN.hostCompany} · NMLS #{CAMPAIGN.hostNmls}
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT — registration card */}
      <div className="relative" id="hero-form-anchor">
        <div className="relative z-10">
          <RegistrationForm prefix="hero-form" showBonuses />
        </div>
      </div>
    </div>
  </section>
));
